/** 富文本工具栏常用色；旁侧为系统取色器（自定义） */

import DOMPurify from 'dompurify';

export const RICH_TEXT_COLOR_PRESETS: ReadonlyArray<{ label: string; value: string }> = [
    { label: '白色', value: '#ffffff' },
    { label: '橙色', value: '#fa8c16' }
];

/** POD 富文本白名单：编辑器实际产生的全部标签 + style 内允许的 CSS 属性 */
const POD_RICH_ALLOWED_TAGS = [
    'p',
    'span',
    'div',
    'b',
    'strong',
    'i',
    'em',
    'u',
    'br',
    'font'
] as const;

/** style 中允许保留的 CSS 属性；超出此列表的 CSS 属性会从 style 串中剔除 */
const POD_RICH_ALLOWED_CSS_PROPERTIES = new Set([
    'color',
    'font-size',
    'font-weight',
    'text-align'
]);

/** style 值黑名单子串：命中即整条 declaration 丢弃，避免 url()/expression()/javascript: 等 CSS 注入 */
const POD_RICH_CSS_VALUE_BLOCKLIST = /(?:url\s*\(|expression\s*\(|javascript:|<)/i;

const filterPodRichStyleValue = (rawStyle: string): string => {
    if (!rawStyle) return '';
    return rawStyle
        .split(';')
        .map((decl) => decl.trim())
        .filter(Boolean)
        .filter((decl) => {
            const idx = decl.indexOf(':');
            if (idx <= 0) return false;
            const prop = decl.slice(0, idx).trim().toLowerCase();
            const value = decl.slice(idx + 1).trim();
            if (!POD_RICH_ALLOWED_CSS_PROPERTIES.has(prop)) return false;
            if (POD_RICH_CSS_VALUE_BLOCKLIST.test(value)) return false;
            return true;
        })
        .join('; ');
};

let podRichHookRegistered = false;
const ensurePodRichHook = (): void => {
    if (podRichHookRegistered) return;
    DOMPurify.addHook('uponSanitizeAttribute', (_node, data) => {
        if (data.attrName !== 'style') return;
        const next = filterPodRichStyleValue(data.attrValue);
        if (!next) {
            data.keepAttr = false;
            data.attrValue = '';
            return;
        }
        data.attrValue = next;
    });
    podRichHookRegistered = true;
};

/**
 * POD 富文本出口 sanitizer：用于 preview 渲染、editor 加载、粘贴整形末端三处 sink。
 * 白名单严格匹配编辑器实际产物（见 POD_RICH_ALLOWED_TAGS / POD_RICH_ALLOWED_CSS_PROPERTIES），
 * 其它标签 / 属性 / CSS 属性 / 危险 CSS 值均剥除。
 */
export function sanitizePodRichHtml(html: string): string {
    if (!html) return '';
    ensurePodRichHook();
    const result = DOMPurify.sanitize(html, {
        ALLOWED_TAGS: [...POD_RICH_ALLOWED_TAGS],
        ALLOWED_ATTR: ['style', 'color'],
        FORBID_TAGS: [
            'script',
            'style',
            'iframe',
            'object',
            'embed',
            'svg',
            'math',
            'details',
            'img',
            'link',
            'meta',
            'video',
            'audio'
        ],
        ALLOW_DATA_ATTR: false,
        ALLOW_UNKNOWN_PROTOCOLS: false
    });
    return typeof result === 'string' ? result : String(result);
}

export const parseCssColorToRgb = (input: string): { r: number; g: number; b: number } | null => {
    const s = input.trim().toLowerCase();
    const hex6 = s.match(/^#([0-9a-f]{6})$/i);
    if (hex6) {
        const n = parseInt(hex6[1], 16);
        return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
    }
    const hex3 = s.match(/^#([0-9a-f]{3})$/i);
    if (hex3) {
        const x = hex3[1];
        return {
            r: parseInt(x[0] + x[0], 16),
            g: parseInt(x[1] + x[1], 16),
            b: parseInt(x[2] + x[2], 16)
        };
    }
    const rgb = s.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
    if (rgb) {
        return { r: Number(rgb[1]), g: Number(rgb[2]), b: Number(rgb[3]) };
    }
    const rgbSp = s.match(/^rgba?\(\s*(\d+)\s+(\d+)\s+(\d+)/i);
    if (rgbSp) {
        return { r: Number(rgbSp[1]), g: Number(rgbSp[2]), b: Number(rgbSp[3]) };
    }
    return null;
};

export const colorsClose = (a: string, b: string, tol = 10): boolean => {
    const pa = parseCssColorToRgb(a);
    const pb = parseCssColorToRgb(b);
    if (!pa || !pb) return false;
    return (
        Math.abs(pa.r - pb.r) <= tol &&
        Math.abs(pa.g - pb.g) <= tol &&
        Math.abs(pa.b - pb.b) <= tol
    );
};

export const getInlineColorAtRangeStart = (root: HTMLElement, range: Range): string | null => {
    const n = range.startContainer;
    let el: HTMLElement | null =
        n.nodeType === Node.TEXT_NODE ? (n.parentElement as HTMLElement | null) : (n as HTMLElement);
    while (el && el !== root) {
        const c = el.style?.color;
        if (c != null && c.trim() !== '' && c !== 'inherit') {
            return c;
        }
        el = el.parentElement;
    }
    return null;
};

export const getActiveColorHintFromEditor = (root: HTMLElement): string | null => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || document.activeElement !== root) return null;
    const range = sel.getRangeAt(0);
    if (!root.contains(range.commonAncestorContainer)) return null;

    const inline = getInlineColorAtRangeStart(root, range);
    if (inline) return inline;

    /** 光标处「实际显示」的颜色，优先于 queryCommand(foreColor)：后者常是上次工具栏点击，与 span 内继承色不一致 */
    const n = range.startContainer;
    const el: Element | null =
        n.nodeType === Node.TEXT_NODE ? (n.parentElement as Element | null) : (n as Element);
    if (el && root.contains(el)) {
        const cc = window.getComputedStyle(el).color;
        if (cc && cc !== 'rgba(0, 0, 0, 0)') {
            return cc;
        }
    }

    try {
        document.execCommand('styleWithCSS', false, 'true');
        const q = document.queryCommandValue('foreColor');
        if (q && typeof q === 'string' && q.length > 0 && q !== 'false') {
            return q;
        }
    } catch {
        /* noop */
    }
    return null;
};

export const matchPresetValue = (colorStr: string | null): string | null => {
    if (!colorStr) return null;
    for (const p of RICH_TEXT_COLOR_PRESETS) {
        if (colorsClose(colorStr, p.value, 18)) return p.value;
    }
    return null;
};

/** 深色底编辑区默认字色为白；浏览器 foreColor 常为黑，避免误判成「自定义」而高亮彩虹钮 */
export const normalizeHintForDarkTextEditor = (hint: string | null): string | null => {
    if (!hint) return null;
    const rgb = parseCssColorToRgb(hint);
    if (rgb && rgb.r <= 48 && rgb.g <= 48 && rgb.b <= 48) {
        return '#ffffff';
    }
    return hint;
};

/** 将解析到的 RGB 转为 #rrggbb，供 type=color 与状态同步 */
export const rgbTripletToHex = (rgb: { r: number; g: number; b: number }): string => {
    const h = (n: number) =>
        Math.max(0, Math.min(255, Math.round(n)))
            .toString(16)
            .padStart(2, '0');
    return `#${h(rgb.r)}${h(rgb.g)}${h(rgb.b)}`;
};

const htmlFragmentHasFontSize = (html: string): boolean => /font-size\s*:/i.test(html);

/**
 * 编辑区用 CSS 设的默认字号不会进入 innerHTML；保存/预览前写入 HTML，使 common_config 与 JSON 预览含字号。
 * 优先给每个 &lt;p&gt; 补 font-size；若无 &lt;p&gt; 则外包一层 div。
 */
export function injectDefaultFontSizeIntoRichTextHtml(html: string, px: number): string {
    const s = (html || '').trim();
    if (!s) return html;
    if (htmlFragmentHasFontSize(s)) return html;
    try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(`<div id="pod-rich-root">${html}</div>`, 'text/html');
        const root = doc.querySelector('#pod-rich-root');
        if (!root) {
            return `<div style="font-size:${px}px">${html}</div>`;
        }
        const ps = root.querySelectorAll('p');
        if (ps.length > 0) {
            ps.forEach((p) => {
                const cur = p.getAttribute('style') || '';
                if (!/font-size\s*:/i.test(cur)) {
                    const next = cur ? `${cur};font-size:${px}px` : `font-size:${px}px`;
                    p.setAttribute('style', next);
                }
            });
            return root.innerHTML;
        }
        return `<div style="font-size:${px}px">${html}</div>`;
    } catch {
        return `<div style="font-size:${px}px">${html}</div>`;
    }
}

/** 祖先为行内容器时，其中的 div 应改为 span，避免出现 &lt;p&gt;&lt;div&gt; 等非法结构 */
const PHRASING_CONTEXT_ANCESTOR_TAGS = new Set([
    'P',
    'SPAN',
    'FONT',
    'B',
    'STRONG',
    'EM',
    'I',
    'U',
    'S',
    'SUB',
    'SUP',
    'A',
    'CODE',
    'MARK',
    'SMALL',
    'LABEL',
    'ABBR',
    'CITE',
    'DEL',
    'INS',
    'Q',
    'KBD',
    'VAR',
    'DATA',
    'TIME',
    'BDI',
    'BDO'
]);

const stripUnsafePasteNodes = (root: HTMLElement): void => {
    root.querySelectorAll('script, style, link, meta, title').forEach((el) => el.remove());
};

const elementDepthWithin = (el: Element, boundary: Element): number => {
    let d = 0;
    let n: Element | null = el;
    while (n && n !== boundary) {
        d += 1;
        n = n.parentElement;
    }
    return d;
};

const hasPhrasingContextAncestor = (el: Element, boundary: Element): boolean => {
    let p: Element | null = el.parentElement;
    while (p && p !== boundary) {
        if (PHRASING_CONTEXT_ANCESTOR_TAGS.has(p.tagName)) {
            return true;
        }
        p = p.parentElement;
    }
    return false;
};

/** 将 div→p / div→span 后可能产生的非法 &lt;p&gt;&lt;p&gt; 展平为单层 */
const flattenNestedParagraphs = (root: HTMLElement): void => {
    for (;;) {
        const inner = root.querySelector('p > p');
        if (!inner || !(inner instanceof HTMLParagraphElement)) break;
        const outer = inner.parentElement;
        if (!outer || outer.tagName !== 'P') break;
        while (inner.firstChild) {
            outer.insertBefore(inner.firstChild, inner);
        }
        outer.removeChild(inner);
    }
};

/**
 * 粘贴 HTML 清理：去掉 script 等，将所有 div 替换为 p（块级）或 span（位于 p/span 等行内上下文中），并展平嵌套 p。
 */
const replaceDivElementsInPasteRoot = (root: HTMLElement): void => {
    const divs = Array.from(root.querySelectorAll('div')).sort(
        (a, b) => elementDepthWithin(b, root) - elementDepthWithin(a, root)
    );
    for (const div of divs) {
        const parent = div.parentElement;
        if (!parent || !root.contains(div)) continue;
        const doc = div.ownerDocument;
        const useSpan = hasPhrasingContextAncestor(div, root);
        const replacement = doc.createElement(useSpan ? 'span' : 'p');
        for (const attr of Array.from(div.attributes)) {
            if (attr.name.toLowerCase() === 'id') continue;
            replacement.setAttribute(attr.name, attr.value);
        }
        while (div.firstChild) {
            replacement.appendChild(div.firstChild);
        }
        parent.replaceChild(replacement, div);
    }
    flattenNestedParagraphs(root);
};

/**
 * 解析剪贴板 HTML（整页或片段），返回可插入编辑区的片段 innerHTML。
 * 末端串接 sanitizePodRichHtml，阻断 <img onerror> / <svg><animate onbegin> 等绕过原黑名单的 XSS 载荷
 */
export function sanitizePastedRichTextHtml(html: string): string {
    const trimmed = (html || '').trim();
    if (!trimmed) return '';
    try {
        const doc = new DOMParser().parseFromString(trimmed, 'text/html');
        const body = doc.body;
        if (!body) return sanitizePodRichHtml(trimmed);
        stripUnsafePasteNodes(body);
        replaceDivElementsInPasteRoot(body);
        return sanitizePodRichHtml(body.innerHTML);
    } catch {
        return sanitizePodRichHtml(trimmed);
    }
}

const spanHasInlineFontSize = (el: HTMLElement): boolean => {
    try {
        if (el.style?.fontSize) return true;
    } catch {
        /* noop */
    }
    const a = el.getAttribute('style');
    return !!(a && /font-size\s*:/i.test(a));
};

const stripInlineFontSizeFromElement = (el: HTMLElement): void => {
    try {
        el.style.removeProperty('font-size');
    } catch {
        /* noop */
    }
    const a = el.getAttribute('style');
    if (!a) return;
    const next = a
        .split(';')
        .map((x) => x.trim())
        .filter(Boolean)
        .filter((part) => !/^font-size\s*:/i.test(part))
        .join('; ');
    if (next) {
        el.setAttribute('style', next);
    } else {
        el.removeAttribute('style');
    }
};

/**
 * 外层已设统一 `font-size` 后，去掉**所有后代**上的内联 `font-size`（不修改 outer 自身），
 * 使多色/多 `span` 选区均继承外层层级字号；否则内层 16px/22px 会继续覆盖新设字号。
 */
export function stripInlineFontSizeFromDescendants(outer: HTMLElement): void {
    const visit = (el: HTMLElement) => {
        stripInlineFontSizeFromElement(el);
        for (let i = 0; i < el.children.length; i++) {
            const c = el.children[i];
            if (c instanceof HTMLElement) {
                visit(c);
            }
        }
    };
    for (let i = 0; i < outer.children.length; i++) {
        const c = outer.children[i];
        if (c instanceof HTMLElement) {
            visit(c);
        }
    }
}

const spanHasInlineColor = (el: HTMLElement): boolean => {
    const a = el.getAttribute('style');
    if (a && /(?:^|;)\s*color\s*:/i.test(a)) {
        return true;
    }
    try {
        const c = el.style.getPropertyValue('color');
        return Boolean(c && c.trim() !== '' && c.trim().toLowerCase() !== 'inherit');
    } catch {
        return false;
    }
};

const stripInlineColorFromElement = (el: HTMLElement): void => {
    try {
        el.style.removeProperty('color');
    } catch {
        /* noop */
    }
    const a = el.getAttribute('style');
    if (!a) return;
    const next = a
        .split(';')
        .map((x) => x.trim())
        .filter(Boolean)
        .filter((part) => !/^color\s*:/i.test(part))
        .join('; ');
    if (next) {
        el.setAttribute('style', next);
    } else {
        el.removeAttribute('style');
    }
};

/** 行内/attribute 的 color 原始串，供与 colorsClose 比较 */
const getElementInlineColorValue = (el: HTMLElement): string | null => {
    const a = el.getAttribute('style');
    if (a) {
        const m = a.match(/(?:^|;)\s*color\s*:\s*([^;]+)/i);
        if (m) {
            const v = m[1]
                .trim()
                .replace(/^['"]|['"]$/g, '');
            if (v) return v;
        }
    }
    try {
        const c = el.style.getPropertyValue('color');
        if (c && c.trim() !== '' && c.toLowerCase() !== 'inherit') {
            return c.trim();
        }
    } catch {
        /* noop */
    }
    return null;
};

const getElementInlineFontSizePx = (el: HTMLElement): number | null => {
    const a = el.getAttribute('style');
    if (a) {
        const m = a.match(/font-size\s*:\s*([^;]+)/i);
        if (m) {
            const n = parseFloat(m[1].trim());
            if (!Number.isNaN(n)) return n;
        }
    }
    try {
        const fs = el.style.fontSize;
        if (fs) {
            const n = parseFloat(fs);
            if (!Number.isNaN(n)) return n;
        }
    } catch {
        /* noop */
    }
    return null;
};

const inlineFontSizesEffectivelyEqual = (a: HTMLElement, b: HTMLElement): boolean => {
    const pa = getElementInlineFontSizePx(a);
    const pb = getElementInlineFontSizePx(b);
    if (pa == null || pb == null) return false;
    return Math.abs(pa - pb) < 0.5;
};

/**
 * 当「外层 span 与唯一子 span」在内联 style 中重复声明了同一类属性时，去掉**外层**声明（内层优先生效），
 * 避免选区内改字号/颜色后产生 `span(旧) > span(新)` 的冗余嵌套。
 * 仅当父子**同色 / 同字号**时才去外层，避免误删橙色父级（例如子级已嵌套为白色时再点白钮）。
 * 目前处理：font-size、color。
 */
export function pruneRedundantNestedParentFontSizeInRichTextRoot(root: HTMLElement): void {
    for (;;) {
        const candidates = Array.from(root.getElementsByTagName('span'));
        let changed = false;
        for (const p of candidates) {
            if (!root.contains(p)) continue;
            if (p.children.length !== 1) continue;
            const c = p.firstElementChild;
            if (!c || c.tagName !== 'SPAN') continue;
            const child = c as HTMLElement;
            const stripFs =
                spanHasInlineFontSize(p) && spanHasInlineFontSize(child) && inlineFontSizesEffectivelyEqual(p, child);
            const c1 = getElementInlineColorValue(p);
            const c2 = getElementInlineColorValue(child);
            const stripColor =
                spanHasInlineColor(p) &&
                spanHasInlineColor(child) &&
                c1 != null &&
                c2 != null &&
                colorsClose(c1, c2, 12);
            if (!stripFs && !stripColor) continue;
            if (stripFs) {
                stripInlineFontSizeFromElement(p);
            }
            if (stripColor) {
                stripInlineColorFromElement(p);
            }
            changed = true;
            break;
        }
        if (!changed) break;
    }
}
